import { useEffect } from 'react';
import { env } from '@/app/config/env';
import { useAppSelector } from '@/app/store/hooks';
import { authApi } from '../../common/api/authService';
import { selectIsAuthenticated } from '../../common/store/authSlice';

/** How often a signed-in shell asks whether its session is still open. */
export const SESSION_CHECK_INTERVAL_MS = 60_000;

/**
 * Signs the user out soon after their session is ended elsewhere (2026-10-13).
 *
 * Deleting a user -- in User Management or with Assigned Admin's Delete Admin, whatever their role -- ends every
 * session they hold, and the platform then refuses their tokens. So does any successful change of their password
 * (NB-552): Change Password, the Security tab or a reset, so every other browser the user is signed in to is
 * returned to the sign-in page here, within a minute or as soon as it is next used. A screen that is only being looked at makes no
 * request, though, so without this the deleted user would stay on it until they next clicked something.
 *
 * So the shell asks `GET /auth/session` every minute, and again whenever the tab is shown or the window regains
 * focus. An ended session answers 401, and the API client's interceptor does what it does for any dead session: the
 * refresh it tries is refused too, the stored tokens are cleared and the user is returned to the sign-in page. This
 * hook decides nothing itself, so a network failure never signs anybody out.
 */
export function useSessionWatch(): void {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  useEffect(() => {
    /* The mock API has no sessions to end. */
    if (!isAuthenticated || env.ENABLE_MOCKS) return undefined;

    let inFlight = false;
    const check = (): void => {
      if (inFlight) return;
      inFlight = true;
      authApi
        .checkSession()
        .catch(() => {
          /* A 401 has already been handled by the interceptor; anything else is not a reason to sign out. */
        })
        .finally(() => {
          inFlight = false;
        });
    };
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') check();
    };

    const timer = window.setInterval(check, SESSION_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', check);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
    };
  }, [isAuthenticated]);
}
